/**
 * Deutsche UI-Texte – zentrale Wahrheit für alle Strings im Mandanten- und
 * Jobcenter-Sichtbereich. Sie-Form, behördentauglich, keine Marketing-Floskeln,
 * keine Coaching-Sprache, keine Emojis.
 *
 * Wenn ein neuer String in die UI muss: erst hier eintragen, dann referenzieren.
 * Hardcodierte Strings in Komponenten sind ein Lint-Hinweis (siehe CLAUDE.md
 * Anti-Pattern-Liste).
 */

export const de = {
  brand: {
    wordmark: 'Petschwork',
    tagline: 'Tragfähigkeitsprüfung nach SGB II',
  },

  app: {
    title: 'Tragfähigkeits-App',
    description:
      'Strukturierte Durchführung der Tragfähigkeitsprüfung nach § 10 Abs. 2 Nr. 5 SGB II.',
  },

  nav: {
    backToHub: 'Zurück zur Übersicht',
    backToCases: 'Zu meinen Fällen',
    profile: 'Profil',
    logout: 'Abmelden',
  },

  login: {
    title: 'TRAG­FÄHIGKEITS-\nPRÜFUNG.',
    subtitle: 'Strukturierte Durchführung der Tragfähigkeitsprüfung nach § 10 Abs. 2 Nr. 5 SGB II.',
    sectionLabel: 'Anmeldung',
    sectionTitle: 'Anmeldung per E-Mail.',
    explainer:
      'Geben Sie Ihre E-Mail-Adresse ein. Sie erhalten einen einmaligen Anmelde-Link. Alternativ können Sie sich mit einem Passwort anmelden.',
    emailLabel: 'E-Mail-Adresse',
    emailPlaceholder: 'name@beispiel.de',
    submit: 'Anmelde-Link anfordern',
    submitting: 'Wird gesendet …',
    successTitle: 'Prüfen Sie Ihr Postfach',
    successBody:
      'Wir haben Ihnen einen Anmelde-Link an die angegebene Adresse geschickt. Der Link ist 60 Minuten gültig.',
    successHintProduction:
      'Sobald Sie auf den Link in der E-Mail klicken, werden Sie automatisch angemeldet und auf die App weitergeleitet.',
    mockConfirmButton: 'Demo-Magic-Link bestätigen',
    mockConfirmHint:
      'Hinweis: In dieser Vor-Version simulieren wir den Link-Klick. Im produktiven System öffnen Sie den Link aus Ihrer E-Mail.',
    devBanner: 'Entwicklungsmodus · Auth wird simuliert · keine echten E-Mails',
    privacyHeadline: 'Hinweis zum Datenschutz',
    privacyBody:
      'Diese Anwendung verarbeitet Sozialdaten nach § 35 SGB I. Die Verarbeitung erfolgt ausschließlich auf Servern in der EU (Frankfurt am Main). Es werden keine Tracking-Pixel, Analyse-Cookies oder externen Schriften eingebunden.',
    privacyLink: 'Vollständige Datenschutz­information',
    avgsHint:
      'Sie können den Beratungsumfang über AVGS (Aktivierungs- und Vermittlungsgutschein) abrechnen lassen, sofern Ihr Jobcenter dies bewilligt.',
    otpFallbackLabel: 'Alternativ: 6-stelligen Code aus der Mail eingeben',
    otpFallbackHint:
      'Falls der Anmelde-Link in einem anderen Browser geöffnet wird oder nicht funktioniert, finden Sie in derselben E-Mail einen 6-stelligen Code. Geben Sie ihn hier ein, um sich anzumelden.',
    otpFallbackPlaceholder: '123456',
    otpFallbackSubmit: 'Code prüfen',
    otpFallbackSubmitting: 'Code wird geprüft …',
    otpFallbackInvalid: 'Bitte geben Sie den 6-stelligen Code aus der E-Mail ein.',
    // Anmelde-Methoden-Umschalter (Magic Link vs. Passwort)
    tabMagicLink: 'Anmelde-Link',
    tabPassword: 'Passwort',
    tabAriaLabel: 'Anmeldemethode wählen',
    // Passwort-Login
    passwordExplainer:
      'Melden Sie sich mit Ihrer E-Mail-Adresse und Ihrem Passwort an. Falls Sie noch kein Passwort festgelegt haben, nutzen Sie den Link unten.',
    passwordLabel: 'Passwort',
    passwordSubmit: 'Anmelden',
    passwordSubmitting: 'Anmeldung läuft …',
    // Passwort vergessen / erstmalig festlegen
    forgotLink: 'Passwort vergessen oder noch keines festgelegt?',
    forgotExplainer:
      'Geben Sie Ihre E-Mail-Adresse ein. Sie erhalten einen Link, mit dem Sie ein neues Passwort festlegen können. Das funktioniert auch, wenn Sie sich bisher nur per Anmelde-Link angemeldet haben.',
    forgotSubmit: 'Link zum Zurücksetzen anfordern',
    forgotSubmitting: 'Wird gesendet …',
    forgotSuccessTitle: 'Prüfen Sie Ihr Postfach',
    forgotSuccessBody:
      'Wir haben Ihnen einen Link zum Festlegen eines neuen Passworts geschickt. Der Link ist 60 Minuten gültig.',
    backToLogin: 'Zurück zur Anmeldung',
    // Konto mit Passwort erstellen
    signupToggle: 'Konto mit Passwort erstellen',
    signupExplainer:
      'Legen Sie ein Konto mit E-Mail-Adresse und Passwort an. Zur Bestätigung erhalten Sie eine E-Mail.',
    signupPasswordRepeatLabel: 'Passwort wiederholen',
    signupSubmit: 'Konto erstellen',
    signupSubmitting: 'Konto wird erstellt …',
    signupSuccessTitle: 'Bestätigen Sie Ihre E-Mail-Adresse',
    signupSuccessBody:
      'Wir haben Ihnen eine Bestätigungs-E-Mail geschickt. Klicken Sie auf den Link in der E-Mail, um Ihr Konto zu aktivieren.',
    haveAccountToggle: 'Ich habe bereits ein Konto',
  },

  // Passwort-Zurücksetzen-Seite (/auth/reset)
  authReset: {
    sectionLabel: 'Anmeldung',
    exchangingTitle: 'LINK WIRD GEPRÜFT…',
    exchangingBody: 'Einen Moment, der Link zum Zurücksetzen wird verifiziert.',
    formTitle: 'NEUES PASSWORT FESTLEGEN.',
    formExplainer:
      'Wählen Sie ein neues Passwort mit mindestens 12 Zeichen. Nach dem Speichern sind Sie direkt angemeldet.',
    passwordLabel: 'Neues Passwort',
    passwordRepeatLabel: 'Neues Passwort wiederholen',
    submit: 'Passwort speichern',
    submitting: 'Wird gespeichert …',
    doneTitle: 'PASSWORT GESPEICHERT.',
    doneBody: 'Sie sind angemeldet und werden gleich weitergeleitet.',
    errorTitle: 'LINK UNGÜLTIG.',
    errorBody:
      'Der Link zum Zurücksetzen ist ungültig oder abgelaufen. Bitte fordern Sie auf der Anmeldeseite einen neuen Link an.',
    backToLogin: 'Zur Anmeldung',
    // PKCE-Fallback (Link in anderem Browser geöffnet)
    recoveryTitle: 'MIT CODE FORTSETZEN.',
    recoveryBody:
      'Der Link wurde offenbar in einem anderen Browser geöffnet als die Anmeldeseite. Geben Sie Ihre E-Mail-Adresse und den 6-stelligen Code aus derselben E-Mail ein, um fortzufahren.',
    recoveryEmailLabel: 'E-Mail-Adresse',
    recoveryOtpLabel: '6-stelliger Code aus der E-Mail',
    recoverySubmit: 'Mit Code fortsetzen',
    recoverySubmitting: 'Code wird geprüft …',
  },

  // Client-seitige Auth-Validierung (Zod)
  authValidation: {
    emailInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
    passwordTooShort: 'Das Passwort muss mindestens 12 Zeichen lang sein.',
    passwordMismatch: 'Die Passwörter stimmen nicht überein.',
  },

  // Magic-Link-/Auth-Callback (/auth/callback)
  authCallback: {
    sectionLabel: 'Anmeldung',
    exchangingTitle: 'ANMELDE-LINK WIRD GEPRÜFT…',
    exchangingBody: 'Einen Moment, der Anmelde-Link wird verifiziert.',
    successTitle: 'ERFOLGREICH ANGEMELDET.',
    successBody: 'Sie werden gleich zur Mandantenliste weitergeleitet.',
    errorTitle: 'ANMELDUNG FEHLGESCHLAGEN.',
    missingCode:
      'Der Anmelde-Link enthält keinen gültigen Code. Bitte fordern Sie einen neuen Link an.',
    unknownError: 'Unbekannter Fehler bei der Anmeldung.',
    backToLogin: 'Zurück zum Login',
    requestNewLink: 'Neuen Link anfordern',
    pkceTitle: 'ANMELDUNG MIT CODE FORTSETZEN.',
    pkceExplainer1:
      'Der Anmelde-Link wurde offenbar in einem anderen Browser oder Mail-Programm geöffnet als die Anmeldeseite. Aus Sicherheitsgründen kann er deshalb nicht direkt verwendet werden.',
    pkceExplainer2Strong: 'Sie müssen den Link aber nicht neu anfordern.',
    pkceExplainer2Rest:
      ' Geben Sie einfach Ihre E-Mail-Adresse und den 6-stelligen Code aus derselben Mail unten ein:',
    pkceEmailLabel: 'E-Mail-Adresse',
    pkceOtpLabel: '6-stelliger Code aus der Mail',
    pkceSubmit: 'Mit Code anmelden',
    pkceSubmitting: 'Code wird geprüft …',
    pkceEmailInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
    pkceOtpInvalid: 'Der Code besteht aus genau 6 Ziffern.',
    pkceVerifyFailed: 'Code konnte nicht verifiziert werden.',
    technicalDetails: 'Technische Details',
  },

  cases: {
    title: 'IHRE FÄLLE.',
    subtitle: 'Übersicht aller Tragfähigkeitsprüfungen, die Sie begonnen oder abgeschlossen haben.',
    empty:
      'Sie haben noch keine Tragfähigkeitsprüfung gestartet. Legen Sie eine neue Akte an, um zu beginnen.',
    newCase: 'Neue Tragfähigkeitsprüfung anlegen',
    openDemo: 'Demo-Akte öffnen',
    columns: {
      reference: 'Aktenzeichen',
      status: 'Status',
      updatedAt: 'Zuletzt geändert',
    },
    sort: {
      label: 'Sortieren',
      updated: 'Zuletzt geändert',
      created: 'Neueste zuerst',
      name: 'Name A–Z',
      status: 'Nach Status',
    },
    changedPrefix: 'geändert',
  },

  hub: {
    title: 'TRAGFÄHIGKEITS-\nPRÜFUNG.',
    subtitle: 'Strukturierte Durchführung der Tragfähigkeitsprüfung nach § 10 Abs. 2 Nr. 5 SGB II.',
    progressLabelSingular: 'Modul abgeschlossen',
    progressLabelPlural: 'Module abgeschlossen',
    sectionLabel: 'Übersicht',
    legalNote:
      'Die hier erhobenen Angaben dienen ausschließlich der Vorbereitung Ihrer Stellungnahme. Eine Übermittlung an das Jobcenter erfolgt nur nach Ihrer ausdrücklichen Freigabe.',
    liveScore: {
      sectionLabel: 'Aktuelle Tragfähigkeitsbewertung',
      scoreLabel: 'Gesamt-Score',
      ofLabel: 'von 100',
      confidenceLabel: 'Datengrundlage',
      confidencePreliminary: 'vorläufig',
      confidenceFinal: 'belastbar',
      trafficGreen: 'Tragfähig',
      trafficYellow: 'Grenzwertig',
      trafficRed: 'Nicht tragfähig',
      hint: 'Der Score aktualisiert sich, sobald Sie weitere Module ausfüllen. Er ist eine Entscheidungsvorbereitung – die finale Stellungnahme wird durch Ihre Beraterin oder Ihren Berater bestätigt (Art. 22 DSGVO).',
      // Mandanten-Sicht: bewusst KEIN Score, keine Ampel, keine Risiken. Der
      // Score ist die fachliche Bewertung des Beraters und darf dem Mandanten
      // nicht als scheinbar automatisierte Ablehnung entgegentreten (Art. 22
      // DSGVO). Stattdessen ein neutrales Erfassungs-Signal.
      mandantSectionLabel: 'Ihre Angaben',
      mandantRecorded: 'Ihre Angaben zu diesem Bereich sind erfasst.',
      mandantEvaluationNote:
        'Die fachliche Einordnung nimmt Ihre Beraterin oder Ihr Berater vor – Sie erhalten keine automatische Bewertung.',
      mandantEmpty: 'Sobald Sie hier etwas eintragen, wird Ihre Angabe automatisch gespeichert.',
    },
  },

  triage: {
    name: 'Triage',
    subtitle: 'Erste Einordnung Ihres Falls.',
  },

  dimensions: {
    identity: {
      name: 'Identität & Qualifikation',
      subtitle: 'Wer Sie sind, was Sie können, woran sich Ihre fachliche Eignung zeigt.',
    },
    activity: {
      name: 'Aktivität & Geschäftsmodell',
      subtitle: 'Was Sie tun, wem Sie es verkaufen, woraus sich Ihr Umsatz speist.',
    },
    style: {
      name: 'Stil & Marktposition',
      subtitle: 'Wie Sie sich am Markt positionieren, was Sie unterscheidbar macht.',
    },
    resources: {
      name: 'Ressourcen & Finanzen',
      subtitle: 'Ertragslage, Liquidität, Bedarfsgemeinschaft – die quantitative Säule.',
    },
    health: {
      name: 'Gesundheit & Resilienz',
      subtitle: 'Belastbarkeit, gesundheitliche Voraussetzungen, Pufferreserven.',
    },
    private: {
      name: 'Privates Umfeld & Bedarfsgemeinschaft',
      subtitle: 'Ihr Lebenskontext, der die Selbständigkeit trägt oder behindert.',
    },
  },

  status: {
    locked: 'Vorheriges Modul abschließen',
    available: 'Modul starten',
    inProgress: 'Fortsetzen',
    complete: 'Abgeschlossen',
    comingSoon: 'Bald verfügbar',
  },

  wizard: {
    breadcrumbBack: 'Übersicht',
    saved: 'Gespeichert',
    saving: 'Wird gespeichert …',
    saveError: 'Konnte nicht speichern – bitte versuchen Sie es erneut.',
    next: 'Weiter',
    previous: 'Zurück',
    finish: 'Modul abschließen',
    skip: 'Später bearbeiten',
    requiredHint: 'Pflichtangabe',
    optionalHint: 'Optional',
    autosaveExplain:
      'Ihre Eingaben werden automatisch zwischengespeichert. Sie können das Modul jederzeit unterbrechen und später fortsetzen.',
  },

  // Step-spezifische Texte
  steps: {
    triage: {
      title: 'TRIAGE.',
      subtitle: 'Erste Einordnung Ihres Falls.',
      legalBasisLabel: 'Rechtlicher Rahmen Ihrer Prüfung',
      legalBasisHint:
        'Wenn Sie unsicher sind: § 10 Abs. 2 Nr. 5 SGB II betrifft Aufstockende Selbständige nach einem Jahr Bürgergeld-Bezug.',
      legalBasisOptions: {
        par10: '§ 10 Abs. 2 Nr. 5 SGB II – Bestandsprüfung nach einem Jahr',
        par16b: '§ 16b SGB II – Einstiegsgeld für Existenzgründer',
        par16c: '§ 16c SGB II – Förderung Selbständiger',
        par93: '§ 93 SGB III – Gründungszuschuss',
        unsure: 'Unsicher – ich brauche eine erste Einschätzung',
      },
    },
    identity: {
      title: 'IDENTITÄT &\nQUALIFIKATION.',
      subtitle: 'Wer Sie sind, was Sie können, woran sich Ihre fachliche Eignung zeigt.',
      qualificationLabel: 'Berufliche Qualifikation und einschlägige Erfahrung',
      qualificationHint:
        'Bitte nennen Sie Ihre Ausbildung, relevante Weiterbildungen, Berufsjahre und – falls zutreffend – Meisterbrief, Approbation oder Zulassung. Diese Angaben fließen in die Bewertung Ihrer fachlichen Tragfähigkeit ein.',
      qualificationPlaceholder:
        'Beispiel: Ausbildung als Steuerfachangestellte (2010), 8 Jahre Berufserfahrung in einer Kanzlei, seit 2024 selbständig in eigener Praxis.',
    },
    activity: {
      title: 'AKTIVITÄT &\nGESCHÄFTSMODELL.',
      subtitle: 'Was Sie tun, wem Sie es verkaufen, woraus sich Ihr Umsatz speist.',
      offeringLabel: 'Beschreibung Ihres Angebots',
      offeringHint:
        'Welche Leistungen oder Produkte verkaufen Sie? An welche Kundengruppe? Wenn Sie mehrere Standbeine haben: bitte das wichtigste zuerst nennen.',
      offeringPlaceholder:
        'Beispiel: Buchhaltung und Lohnabrechnung für kleine Handwerksbetriebe in der Region.',
    },
    style: {
      title: 'STIL &\nMARKTPOSITION.',
      subtitle: 'Wie Sie sich am Markt positionieren, was Sie unterscheidbar macht.',
      positioningLabel: 'Was unterscheidet Sie von Ihrem Wettbewerb?',
      positioningHint:
        'Bitte beschreiben Sie nüchtern – nicht werblich – warum Kundinnen und Kunden zu Ihnen kommen und nicht zur Konkurrenz.',
      positioningPlaceholder:
        'Beispiel: Persönliche Betreuung, kurze Reaktionszeiten, Vor-Ort-Termine bei Mandanten ohne Aufpreis.',
    },
    resources: {
      title: 'RESSOURCEN &\nFINANZEN.',
      subtitle: 'Ertragslage, Liquidität, Bedarfsgemeinschaft – die quantitative Säule.',
      placeholderHeading: 'Dieses Modul wird gerade ausgebaut.',
      placeholderBody:
        'Die vollständige Erfassung von BWA-Eckdaten, Stundensatz, Liquiditätsreichweite und Bedarfsgemeinschaft folgt im nächsten Schritt. Aktuell können Sie die übrigen Module bearbeiten.',
      // Sub-Step-Navigation (5.1 – 5.5) — Phase B Renumbering
      subSteps: {
        bwa: {
          short: '5.1',
          name: 'BWA-Eckdaten',
          intro:
            'Bitte tragen Sie die durchschnittlichen monatlichen Werte aus Ihrer letzten BWA oder EKS ein. Sie können Beträge auch grob schätzen – die Stellungnahme weist die Datenquelle aus.',
          revenueLabel: 'Monatlicher Umsatz (netto)',
          revenueHint: 'Mittelwert der letzten verfügbaren Monate.',
          costsLabel: 'Monatliche Betriebsausgaben',
          costsHint: 'Summe aller laufenden Kosten ohne Privatentnahmen.',
          profitLabel: 'Daraus errechneter Gewinn',
          profitHint: 'Wird automatisch berechnet (Umsatz minus Kosten).',
        },
        rate: {
          short: '5.2',
          name: 'Bereinigter Stundensatz',
          intro:
            'Wieviele Stunden im Monat können Sie tatsächlich als bezahlte Arbeitszeit ansetzen? Krankheits-, Akquise- und Verwaltungszeiten zählen nicht dazu.',
          hoursLabel: 'Abrechenbare Stunden pro Monat',
          hoursHint: 'Realistische, dauerhaft tragfähige Auslastung – keine Spitzenwoche.',
          rateLabel: 'Bereinigter Stundensatz',
          rateHint:
            'Gewinn pro abrechenbare Stunde. Liegt er unter der branchenüblichen Untergrenze, ist die Tätigkeit nicht tragfähig.',
        },
        liquidity: {
          short: '5.3',
          name: 'Liquiditätsreichweite',
          intro:
            'Wie lange können Sie laufende Kosten aus eigenen Mitteln decken, falls Umsätze ausbleiben?',
          assetsLabel: 'Liquide Mittel zum Stichtag',
          assetsHint: 'Geschäftskonto, kurzfristige Rücklagen, keine Sachwerte.',
          liabilitiesLabel: 'Kurzfristige Verbindlichkeiten',
          liabilitiesHint:
            'Offene Rechnungen, fällige Steuern, Kontokorrent — mindern die verfügbare Netto-Liquidität.',
          burnLabel: 'Monatlicher Mittelabfluss (Burn-Rate)',
          burnHint:
            'Voreingestellt mit den monatlichen Kosten aus Schritt 4.1. Sie können den Wert anpassen, wenn relevant.',
          runwayLabel: 'Reichweite in Monaten',
          runwayHint: 'Liquide Mittel geteilt durch Burn-Rate.',
        },
        bg: {
          short: '5.4',
          name: 'Bedarfsgemeinschaft',
          intro:
            'Wer lebt mit Ihnen im Haushalt und wie hoch sind die Wohnkosten? Diese Angaben fließen in die Berechnung des SGB-II-Bedarfs.',
          membersLabel: 'Mitglieder der Bedarfsgemeinschaft',
          addMember: 'Person hinzufügen',
          removeMember: 'Entfernen',
          memberRoleLabel: 'Rolle',
          memberAgeLabel: 'Alter',
          memberPregnantLabel: 'Schwangerschaft (§ 21 Abs. 2 SGB II)',
          memberSingleParentLabel: 'Alleinerziehend (§ 21 Abs. 3 SGB II)',
          roleAntragsteller: 'Antragstellerin / Antragsteller',
          rolePartner: 'Partnerin / Partner',
          roleKind: 'Kind',
          kduLabel: 'Kosten der Unterkunft (KdU) pro Monat',
          kduKaltLabel: 'Kaltmiete',
          kduNebenLabel: 'Nebenkosten',
          kduHeizLabel: 'Heizkosten',
          bedarfLabel: 'Errechneter Bedarf der Bedarfsgemeinschaft',
          bedarfHint: 'Regelbedarfe nach § 20 SGB II + Mehrbedarfe + Kosten der Unterkunft.',
          incomeSectionLabel: 'Anrechenbares Einkommen (§ 11 SGB II)',
          incomeSectionHint:
            'Grundlage der Tragfähigkeitslücke. Das Erwerbseinkommen wird mit den Freibeträgen nach § 11b angerechnet, sonstiges Einkommen voll.',
          bruttoLabel: 'Brutto-Erwerbseinkommen pro Monat',
          bruttoHint:
            'Bei Selbständigen der nach § 3 Alg II-V ermittelte Wert (Betriebseinnahmen − Betriebsausgaben). Leer lassen, wenn noch nicht ermittelt — dann wird der Gewinn als Näherung verwendet.',
          sonstigesLabel: 'Sonstiges anrechenbares Einkommen pro Monat',
          sonstigesHint:
            'Kindergeld, Elterngeld, Unterhalt, Renten — voll auf den Bedarf angerechnet.',
        },
        luecke: {
          short: '5.5',
          name: 'Tragfähigkeitslücke',
          intro:
            'Aus den Angaben der vorherigen Schritte ergibt sich, ob Ihre Selbständigkeit den Bedarf der Bedarfsgemeinschaft deckt.',
          gapLabel: 'Monatliche Tragfähigkeitslücke',
          gapHintCovered: 'Ihre Selbständigkeit deckt aktuell den Bedarf der Bedarfsgemeinschaft.',
          gapHintUncovered:
            'Diese Lücke entspricht dem ergänzenden Bürgergeld-Anspruch nach § 19 SGB II.',
          bedarfLine: 'Bedarf BG',
          einkommenLine: 'Anzurechnendes Einkommen',
          gapLine: 'Differenz',
          finishCta: 'Modul 05 abschließen',
          // Mandanten-Sicht: sachliche Übersicht der eigenen Zahlen ohne
          // alarmierende Hero-Darstellung; die Bewertung nimmt der Berater vor.
          mandantIntro:
            'Diese Übersicht fasst Ihre eigenen Angaben aus den vorherigen Schritten zusammen. Sie dient Ihrer Orientierung – die fachliche Bewertung nimmt Ihre Beraterin oder Ihr Berater vor.',
          mandantOverviewLabel: 'Ihre Zahlen im Überblick',
        },
      },
      // Live-Score-Footer (auf jeder Sub-Step-Seite sichtbar)
      liveScore: {
        sectionLabel: 'Live-Bewertung',
        scoreLabel: 'Score Ressourcen',
        confidenceLabel: 'Datengrundlage',
        risksLabel: 'Wichtigste Hinweise',
        noRisks: 'Keine Auffälligkeiten anhand der bisher erfassten Daten.',
        ofLabel: 'von 100',
        trafficGreen: 'Tragfähig',
        trafficYellow: 'Grenzwertig',
        trafficRed: 'Nicht tragfähig',
      },
    },
    health: {
      title: 'GESUNDHEIT &\nRESILIENZ.',
      subtitle: 'Belastbarkeit, gesundheitliche Voraussetzungen, Pufferreserven.',
      capacityLabel: 'Wochenarbeitszeit, die Sie zuverlässig leisten können',
      capacityHint:
        'Bitte geben Sie eine realistische, dauerhaft tragbare Wochenarbeitszeit an – keine Spitzenwoche. Falls eine ärztliche Einschränkung vorliegt, dürfen Sie das hier kurz vermerken.',
      capacityPlaceholder:
        'Beispiel: ca. 35 Stunden pro Woche, eingeschränkt durch chronische Rückenbeschwerden.',
    },
    private: {
      title: 'PRIVATES UMFELD &\nBEDARFS­GEMEINSCHAFT.',
      subtitle: 'Ihr Lebenskontext, der die Selbständigkeit trägt oder behindert.',
      contextLabel: 'Wer lebt mit Ihnen im Haushalt? Welche Verpflichtungen prägen Ihren Alltag?',
      contextHint:
        'Bitte geben Sie an, wie viele Personen zu Ihrer Bedarfsgemeinschaft gehören, ob Kinder zu betreuen sind und ob es pflegebedürftige Angehörige gibt. Die Detail-Eingabe folgt in Modul 05 (Ressourcen & Finanzen).',
      contextPlaceholder:
        'Beispiel: Bedarfsgemeinschaft mit Partnerin und einem Kind (8 Jahre). Keine pflegebedürftigen Angehörigen.',
    },
  },

  cockpit: {
    kicker: 'Beratung',
    title: 'BERATER-COCKPIT.',
    subtitle: 'Aktive Mandate und Stellungnahme-Entwürfe.',
    empty: 'Es liegen aktuell keine Mandate vor.',
    caseContextLabel: 'Mandat',
  },

  // Fehler- und Bestätigungstexte des Cockpits (Generator + Review-Workflow)
  cockpitErrors: {
    demoFourEyes: 'Vier-Augen-Prinzip ist im Demo-Modus nicht verfügbar.',
    assessmentsIncomplete:
      'Bewertungstexte sind unvollständig. Bitte den Editor unten erst vollständig ausfüllen.',
    reviewRequestFailed: 'Review konnte nicht angefordert werden.',
    caseNotFound:
      'Mandat nicht gefunden. Wurde der Case gelöscht oder fehlt Ihnen die Berechtigung?',
    missingDataTitle: 'Stellungnahme braucht noch folgende Daten:',
    missingDataFootnote:
      'Tipp: Stammdaten-Form bei „Mandat anlegen“, Bewertungstexte unten im Editor.',
    validationTitle: '{count} Validierungsfehler:',
    confidenceAborted:
      'Generierung abgebrochen — Konfidenz {confidence} % unter dem Mindestwert 60 %. Bitte fehlende Unterlagen beim Mandanten anfordern und erneut versuchen.',
    generateUnknown: 'Unbekannter Fehler bei der Generierung.',
    popupBlocked: 'Pop-up wurde blockiert.',
  },

  // Globales Toast-System
  toast: {
    closeLabel: 'Meldung schließen',
  },

  // Felder der ausgebauten Dimensions-Wizard-Schritte (identity/activity/style/
  // health/private). Freitext-Dimensionen, gespeichert als fields:Record<string,string>.
  dimensionFields: {
    common: {
      notesLabel: 'Ergänzende Anmerkungen',
      notesHint: 'Optionaler Freitext für Kontext, der in keine der Kategorien passt.',
      notesPlaceholder: 'Optional …',
      yes: 'Ja',
      no: 'Nein',
    },
    identity: {
      qualificationsLabel: 'Qualifikationen & Abschlüsse',
      qualificationsHint:
        'Ausbildung, Studium, Meister, Weiterbildungen. Markieren Sie, was für Ihre selbständige Tätigkeit einschlägig ist.',
      qualAddLabel: 'Qualifikation hinzufügen',
      qualIndustryRelevant: 'einschlägig',
      qualTypeOptions: {
        ausbildung: 'Ausbildung',
        studium: 'Studium',
        meister: 'Meister',
        weiterbildung: 'Weiterbildung',
        sonstige: 'Sonstige',
      },
      industryExperienceYearsLabel: 'Einschlägige Berufserfahrung',
      industryExperienceYearsSuffix: 'Jahre',
      hasEntrepreneurialExperienceLabel: 'Haben Sie unternehmerische Vorerfahrung?',
      yearsSelfEmployedLabel: 'Davon Jahre selbständig',
      yearsSelfEmployedSuffix: 'Jahre',
      uspLabel: 'Alleinstellungsmerkmal (USP)',
      uspHint:
        'Was können Sie, das andere nicht (so) können? Woran zeigt sich Ihre fachliche Eignung?',
      uspPlaceholder: 'Beispiel: Einzige zertifizierte Fachkraft für … in der Region.',
      personalTraitsLabel: 'Unternehmerische Eigenschaften',
      personalTraitsHint: 'Wählen Sie die Eigenschaften, die Ihre Selbständigkeit tragen.',
      traitOptions: {
        gewissenhaftigkeit: 'Gewissenhaftigkeit',
        zielorientierung: 'Zielorientierung',
        durchhaltevermoegen: 'Durchhaltevermögen',
        durchsetzungsstaerke: 'Durchsetzungsstärke',
        risikotoleranz: 'Risikotoleranz',
        akquisestaerke: 'Akquisestärke',
        lernbereitschaft: 'Lernbereitschaft',
      },
    },
    activity: {
      valuePropositionLabel: 'Nutzenversprechen',
      valuePropositionHint:
        'Welchen konkreten Nutzen bieten Sie? Welches Problem lösen Sie für wen?',
      valuePropositionPlaceholder:
        'Beispiel: Ich entlaste Handwerksbetriebe bei der Lohnbuchhaltung …',
      targetCustomersLabel: 'Kundengruppe',
      targetCustomersHint: 'Wer kauft bei Ihnen? Beschreiben Sie Ihre typische Kundschaft.',
      topClientsLabel: 'Auftraggeber-Konzentration (Klumpenrisiko)',
      topClientsHint:
        'Ihre größten Auftraggeber nach Umsatzanteil. Wichtig für die Abhängigkeits-Bewertung — ein Kunde über 50 % ist ein Risiko.',
      topClientsAddLabel: 'Auftraggeber hinzufügen',
      hasRecurringRevenueLabel: 'Haben Sie wiederkehrende Umsätze (z.B. Verträge, Abos)?',
      recurringShareLabel: 'Anteil wiederkehrender Umsätze',
      recurringShareSuffix: '% des Umsatzes',
      scalabilityLabel: 'Skalierbarkeit',
      scalabilityHint: 'Kann Ihr Geschäft wachsen, ohne dass Ihre Arbeitszeit 1:1 mitwächst?',
      scalabilityOptions: {
        linear: 'Linear (Umsatz ≈ meine Arbeitszeit)',
        partly_scalable: 'Teilweise skalierbar',
        highly_scalable: 'Stark skalierbar',
        unknown: 'Unklar',
      },
      hasDocumentedSalesProcessLabel: 'Gibt es einen dokumentierten Vertriebs-/Akquiseprozess?',
    },
    style: {
      targetGroupDefinedLabel: 'Ist Ihre Zielgruppe klar definiert?',
      targetGroupDescriptionLabel: 'Beschreibung der Zielgruppe',
      targetGroupDescriptionHint: 'Wen sprechen Sie gezielt an? Je konkreter, desto besser.',
      competitorsLabel: 'Wettbewerber',
      competitorsHint: 'Ein Wettbewerber pro Zeile. Wer bietet Ähnliches an?',
      competitorsPlaceholder: 'Wettbewerber 1\nWettbewerber 2',
      differentiatorsLabel: 'Unterscheidungsmerkmale',
      differentiatorsHint: 'Was macht Sie gegenüber dem Wettbewerb unterscheidbar?',
      selfRateLabel: 'Ihr Netto-Stundensatz',
      selfRateSuffix: '€ / Stunde',
      floorRateLabel: 'Branchenübliche Untergrenze',
      floorRateHint: 'Falls bekannt — der übliche Mindest-Stundensatz Ihrer Branche.',
      floorRateSuffix: '€ / Stunde',
      pricingStrategyDocumentedLabel: 'Ist Ihre Preisbildung dokumentiert/begründet?',
    },
    health: {
      sensitiveNote:
        'Gesundheitsdaten nach Art. 9 DSGVO. Datensparsam erfasst, verschlüsselt gespeichert, nur für die Tragfähigkeitsprüfung genutzt.',
      resilienceLabel: 'Selbsteinschätzung Belastbarkeit (1–10)',
      resilienceHint:
        '1 = sehr gering, 10 = sehr hoch. Wie belastbar schätzen Sie sich aktuell ein?',
      hoursPerWeekLabel: 'Arbeitszeit pro Woche',
      hoursPerWeekSuffix: 'h / Woche',
      hasMedicalCertificationLabel: 'Liegt eine ärztliche Bescheinigung zur Belastbarkeit vor?',
      chronicConditionsLabel: 'Bestehen chronische Erkrankungen?',
      conditionsAffectWorkLabel: 'Beeinträchtigen gesundheitliche Umstände die Tätigkeit?',
      copingLabel: 'Umgang mit Belastung',
      copingHint: 'Wie gehen Sie mit Stress/Belastungsspitzen um? Welche Strategien haben Sie?',
      pastCrisisLabel: 'Frühere Belastungssituationen',
      pastCrisisHint: 'Wie haben Sie frühere schwierige Phasen gemeistert? (optional)',
    },
    private: {
      sensitiveNote:
        'Angaben zum privaten Umfeld werden datensparsam erhoben und nur erfasst, soweit für die Prüfung nach § 10 SGB II nötig.',
      childcareStatusLabel: 'Kinderbetreuung',
      childcareStatusHint: 'Ist die Betreuung von Kindern gesichert, falls relevant?',
      childcareOptions: {
        not_applicable: 'Nicht zutreffend (keine Kinder)',
        secured: 'Gesichert',
        not_secured: 'Nicht gesichert',
      },
      hasCareResponsibilityLabel: 'Bestehen Pflegeverantwortungen im Haushalt?',
      careDescriptionLabel: 'Beschreibung der Pflegesituation',
      careDescriptionHint: 'Falls zutreffend — Umfang und Auswirkung auf Ihre Verfügbarkeit.',
      zumutbarkeitLabel: 'Zumutbarkeitsgründe (§ 10 Abs. 1 SGB II)',
      zumutbarkeitHint:
        'Gründe, die für die Fortführung der Selbständigkeit sprechen (Betreuung, Pflege, Ortsbindung, gesundheitliche Gründe …).',
      regionalContextLabel: 'Regionaler Arbeitsmarkt-Kontext',
      regionalContextHint: 'Besonderheiten Ihres regionalen Arbeitsmarkts, die relevant sind.',
    },
  },

  // Berater-Sicht: fachliche Bearbeitung einer Dimension (Zwei-Ebenen-Modell)
  beraterReview: {
    sectionLabel: 'Fachliche Bearbeitung',
    sectionTitle: 'DIMENSIONEN FACHLICH BEARBEITEN.',
    sectionIntro:
      'Ergänzen Sie die Angaben des Mandanten aus fachlicher Sicht. Der wirksame Wert (Ihre Eingabe, sonst die Mandanten-Angabe) fließt in Score und Stellungnahme. Ihre Bearbeitung ist für den Mandanten nicht sichtbar.',
    mandantPanelLabel: 'Angaben des Mandanten',
    mandantEmpty: 'Keine Angabe des Mandanten.',
    adoptButton: 'Angabe übernehmen',
    adoptAriaLabel: 'Mandanten-Angabe für „{field}“ übernehmen',
    beraterFieldLabel: 'Ihre fachliche Eingabe',
    effectiveMandant: 'Wirksam: Angabe des Mandanten',
    effectiveBerater: 'Wirksam: Ihre Eingabe',
    effectiveEmpty: 'Noch kein Wert erfasst',
    markReviewedButton: 'Als geprüft markieren',
    markedReviewedButton: 'Fachlich geprüft',
    mandantUpdatedAt: 'Mandant zuletzt bearbeitet: {date}',
    statusNone: 'Keine Mandanten-Angaben',
    statusUnreviewed: 'Zu prüfen',
    statusStale: 'Neue Angaben seit Prüfung',
    statusReviewed: 'Fachlich geprüft',
    openDetail: 'Einzelansicht',
    backToCockpit: 'Zurück zum Cockpit',
    singleKicker: 'Fachliche Bearbeitung',
    singleSubtitle:
      'Angaben des Mandanten (read-only) neben Ihrer fachlichen Bearbeitung. Ihre Eingabe ist für den Mandanten nicht sichtbar.',
    dimensionNotFound: 'Diese Dimension kann fachlich nicht bearbeitet werden.',
  },

  // Zentrale Bestätigungs-Dialoge (Ersatz für window.confirm)
  confirmDialog: {
    defaultConfirmLabel: 'Bestätigen',
    defaultCancelLabel: 'Abbrechen',
    overwriteTitle: 'Vorhandenen Text überschreiben?',
    overwriteBody: 'Der Vorschlagstext ersetzt Ihren vorhandenen Text in diesem Feld.',
    overwriteConfirmLabel: 'Überschreiben',
    llmOverwriteBody: 'Der generierte KI-Vorschlag ersetzt Ihren vorhandenen Text in diesem Feld.',
    resetAssessmentsTitle: 'Alle eigenen Bewertungstexte löschen?',
    resetAssessmentsBody:
      'Alle von Ihnen erfassten Bewertungstexte dieses Mandats werden entfernt. Dieser Schritt kann nicht rückgängig gemacht werden.',
    resetAssessmentsConfirmLabel: 'Löschen',
    resolveClarificationTitle: 'Rückfrage als gelöst markieren?',
    resolveClarificationConfirmLabel: 'Als gelöst markieren',
    removeDocumentTitle: 'Datei entfernen?',
    removeDocumentConfirmLabel: 'Entfernen',
  },

  // Verbindliche Berater-Verantwortung vor der Generierung (Art. 22 DSGVO /
  // keine automatisierte Einzelfallentscheidung). Der Score ist nur
  // Entscheidungsvorbereitung — die Stellungnahme trägt der Berater.
  cockpitResponsibility: {
    label:
      'Ich habe die Angaben fachlich geprüft und verantworte diese Einordnung. Sie erfolgt nicht automatisiert; die generierte Stellungnahme gebe ich unter meinem Namen und meiner Unterschrift ab.',
    hintUnchecked: 'Bitte bestätigen Sie Ihre fachliche Verantwortung, bevor Sie generieren.',
  },

  // Konfidenz-Vorbehalt vor der Generierung (< 60 % Bewertungsgrundlage)
  cockpitConfidenceConfirm: {
    title: 'Konfidenz unter Mindestwert',
    body: 'Konfidenz nur {confidence} % — die Datenbasis ist nicht abschließend. Eine formelle Stellungnahme nach FW-BA-Standard erfordert mindestens 60 % Konfidenz. Empfohlen: Nachforderung weiterer Unterlagen beim Mandanten oder Jobcenter.',
    question: 'Möchten Sie trotzdem generieren?',
    confirmLabel: 'Trotzdem generieren',
    cancelLabel: 'Abbrechen',
  },

  // C3 — Mandanten-Self-Service-Portal
  invitations: {
    sectionLabel: 'Mandanten-Self-Service',
    sectionTitle: 'MANDANT EINLADEN.',
    sectionIntro:
      'Senden Sie dem Mandanten einen Einladungs-Link per E-Mail. Nach Klick kann er seine Geschäfts- und Bedarfsdaten direkt in der App eintragen.',
    dsgvoNote:
      'Der Mandant erhält Zugriff auf den Wizard zu diesem Mandat. Bewertungen und Stellungnahme-Versionen bleiben für den Mandanten verborgen.',
    emailLabel: 'E-Mail-Adresse des Mandanten',
    emailPlaceholder: 'name@beispiel.de',
    sendButton: 'Einladung senden →',
    sendingButton: 'Wird gesendet …',
    resendButton: 'Erneut senden',
    revokeButton: 'Widerrufen',
    revokeAccessButton: 'Zugang entziehen',
    statusInvitedAt: 'Einladung an {email} gesendet am {date}',
    statusValidUntil: 'gültig bis {date}',
    statusRedeemed: 'Mandant {email} hat den Zugang am {date} aktiviert',
    statusLastActivity: 'Letzte Wizard-Aktivität: {date}',
    statusNoActivity: 'Bisher noch keine Daten eingetragen',
    statusRevoked: 'Zugang entzogen am {date}',
    errorRevokeFailed: 'Widerruf fehlgeschlagen.',
    errorSendFailed: 'Einladung konnte nicht versendet werden.',
    errorAccessRevokeFailed: 'Zugang konnte nicht entzogen werden.',
    confirmRevokeAccessTitle: 'Zugang entziehen?',
    confirmRevokeAccess:
      'Den Zugang dieses Mandanten zum Mandat wirklich entziehen? Bereits eingetragene Daten bleiben erhalten, der Mandant hat aber keine Sicht mehr darauf.',
    confirmRevokeAccessConfirm: 'Zugang entziehen',
  },

  mandantBoard: {
    sectionLabel: 'Datenerfassung durch Mandant',
    sectionTitle: 'STAND DER MANDANTEN-EINGABEN.',
    sectionIntro: 'Live-Ansicht der Module, die der Mandant über sein Self-Service-Portal befüllt.',
    statusComplete: 'Abgeschlossen',
    statusInProgress: 'In Bearbeitung',
    statusEmpty: 'Offen',
    bwaTitle: 'BWA-Upload',
    bwaUploaded: 'BWA hochgeladen am {date}',
    bwaMissing: 'Noch keine BWA-Datei',
    lastEdit: 'Zuletzt bearbeitet {date}',
    noEditYet: 'Noch nicht bearbeitet',
    errorLoadFailed: 'Status konnte nicht geladen werden.',
    refreshButton: 'Aktualisieren',
    // Stammdaten-Status (Phase A)
    stammdatenLabel: 'Stammdaten',
    stammdatenComplete: 'Erfasst',
    stammdatenIncomplete: 'Noch unvollständig',
    stammdatenEmpty: 'Nicht erfasst',
    stammdatenShowDetails: 'Details ansehen',
    stammdatenHideDetails: 'Details ausblenden',
    // Doc-Typen-Status
    docsLabel: 'Dokumente',
    docNotUploaded: 'Nicht hochgeladen',
    docUploadedAt: 'Hochgeladen am {date}',
    docDownload: 'Herunterladen',
    docDownloadError: 'Download-Link konnte nicht erstellt werden.',
  },

  // Cockpit-Sektion „Rückfragen" — Berater-Sicht
  cockpitClarifications: {
    sectionLabel: 'Rückfragen',
    sectionTitle: 'RÜCKFRAGEN AN/VOM MANDANT.',
    sectionIntro:
      'Asynchroner Channel pro Wizard-Feld. Mandant bekommt eine Benachrichtigung — Antworten landen hier zur Freigabe.',
    tabOpen: 'Offene Rückfragen',
    tabAwaiting: 'Beantwortet · warten auf Freigabe',
    tabResolved: 'Gelöst (Verlauf)',
    emptyOpen: 'Aktuell keine offenen Rückfragen.',
    emptyAwaiting: 'Aktuell keine beantworteten Rückfragen.',
    emptyResolved: 'Noch keine Rückfragen gelöst.',
    askedByMandant: 'Mandant',
    askedByBerater: 'Sie / Ihr Team',
    askedAt: 'gefragt am {date}',
    answeredAt: 'beantwortet am {date}',
    resolvedAt: 'gelöst am {date}',
    answerInline: 'Inline antworten',
    answerLabel: 'Ihre Antwort an den Mandanten',
    answerPlaceholder: 'Beschreiben Sie kurz, was der Mandant tun soll oder welche Info fehlt.',
    sendAnswer: 'Antwort senden →',
    resolveButton: 'Als gelöst markieren',
    resolveConfirm: 'Diese Rückfrage als endgültig gelöst markieren?',
    resolving: 'Wird markiert …',
    sending: 'Wird gesendet …',
    error: 'Aktion fehlgeschlagen.',
    newButton: '+ Neue Rückfrage stellen',
    newModalTitle: 'Neue Rückfrage an den Mandanten',
    newFieldLabel: 'Feld / Thema',
    newFieldPlaceholder: 'Bitte Feld auswählen …',
    newQuestionLabel: 'Ihre Frage',
    newQuestionPlaceholder:
      'Beispiel: Bitte BWA für Q1 2026 nachreichen — die hochgeladene Datei betrifft nur Q4 2025.',
    newSubmit: 'Rückfrage senden →',
    newCancel: 'Abbrechen',
    newError: 'Rückfrage konnte nicht angelegt werden.',
    countBadge: 'Mandant antwortet noch auf {count} Rückfrage(n)',
    countBadgeAwaiting: '{count} Rückfrage(n) zur Freigabe',
    onField: 'Zum Feld',
    jumpToWizard: 'Im Wizard öffnen →',
  },

  mandant: {
    redeem: {
      title: 'WILLKOMMEN.',
      subtitle: 'Sie wurden eingeladen, an Ihrer Tragfähigkeitsprüfung mitzuwirken.',
      missingTokenTitle: 'UNGÜLTIGER LINK.',
      missingTokenBody:
        'Dieser Einladungs-Link ist unvollständig. Bitte verwenden Sie den Link aus der E-Mail Ihrer Beraterin oder Ihres Beraters.',
      notLoggedInTitle: 'BITTE ANMELDEN.',
      notLoggedInBody:
        'Klicken Sie zuerst auf den Anmelde-Link in der E-Mail. Danach landen Sie automatisch wieder hier.',
      consentLabel:
        'Mit Klick auf „Einladung annehmen" bestätigen Sie, dass Ihr Berater Ihre Daten zur Tragfähigkeitsprüfung verarbeiten darf (§ 35 SGB I, Art. 9 DSGVO).',
      acceptButton: 'Einladung annehmen →',
      acceptingButton: 'Wird verarbeitet …',
      successTitle: 'ZUGANG AKTIVIERT.',
      successBody: 'Sie werden gleich zu Ihren Modulen weitergeleitet.',
      errorTitle: 'EINLADUNG NICHT GÜLTIG.',
      errorExpiredBody:
        'Ihre Einladung ist abgelaufen oder bereits eingelöst. Bitte fragen Sie Ihren Berater nach einer neuen Einladung.',
    },
    home: {
      sectionLabel: 'Ihre Tragfähigkeitsprüfung',
      title: 'MEINE TRAG­FÄHIGKEITS-\nPRÜFUNG.',
      subtitle:
        'Bitte tragen Sie Ihre Geschäfts- und Bedarfsdaten ein. Sie können jederzeit unterbrechen — wir speichern Ihre Eingaben automatisch.',
      modulesHeading: 'Ihre Module',
      uploadHeading: 'Betriebswirtschaftliche Auswertung (BWA)',
      uploadIntro:
        'Falls Sie eine aktuelle BWA als XLSX oder PDF haben, laden Sie sie hier hoch. Sie ist die wichtigste Grundlage für die Bewertung Ihrer Ertragslage.',
      footerHint:
        'Ihr Berater erhält automatisch eine Benachrichtigung, sobald Sie alle Module abgeschlossen haben.',
      notFoundTitle: 'KEIN ZUGANG.',
      notFoundBody:
        'Dieses Mandat existiert nicht oder Ihr Zugang wurde entzogen. Bitte wenden Sie sich an Ihren Berater.',
      // Transienter Ladefehler (Netzwerk) — bewusst NICHT „Zugang entzogen".
      loadErrorTitle: 'VERBINDUNG UNTERBROCHEN.',
      loadErrorBody:
        'Ihre Daten konnten gerade nicht geladen werden. Das liegt meist an der Internetverbindung – Ihre Angaben sind gespeichert. Bitte versuchen Sie es erneut.',
      loadErrorRetry: 'Erneut versuchen',
      // Vertrauensanker im Header
      headerSubtitle: 'Eine fachkundige Stelle nach AVGS § 45 SGB III',
      // Pause-Hinweis (Re-Login-friendly)
      pauseHint:
        'Sie können jederzeit Ihre Sitzung schließen. Beim nächsten Login finden Sie Ihre Eingaben genau dort wieder, wo Sie aufgehört haben.',
      // Übersicht & Bestätigung
      overviewCtaLabel: 'Ich bin fertig',
      overviewCtaButton: 'Übersicht & Bestätigung →',
      overviewCtaHint:
        'Verfügbar, sobald Sie mindestens 80 % Ihrer Module bearbeitet haben — Sie können die Übersicht später erneut aufrufen.',
      // Onboarding-Tour
      restartTour: 'Einführung erneut anzeigen',
    },
    wizard: {
      title: 'IHRE DATEN.',
      breadcrumbBack: 'Zur Übersicht',
      footerHint: 'Bitte vervollständigen Sie das Modul, um zum nächsten zu gelangen.',
    },
    overview: {
      sectionLabel: 'Übersicht & Bestätigung',
      title: 'IHRE EINGABEN\nIM ÜBERBLICK.',
      subtitle:
        'Bitte prüfen Sie alle Angaben sorgfältig. Sie können jeden Eintrag noch einmal anpassen, bevor Sie an Ihren Berater übergeben.',
      backLink: 'Zur Mandanten-Übersicht',
      editLink: 'Ändern',
      noValue: '— noch nicht ausgefüllt',
      moduleStatusComplete: 'Abgeschlossen',
      moduleStatusInProgress: 'In Bearbeitung',
      moduleStatusEmpty: 'Noch offen',
      missingTitle: 'Noch nicht vollständig.',
      missingIntro:
        'Bevor Sie übergeben können, befüllen Sie bitte mindestens die unten markierten Module. Pflichtangabe ist ausschließlich die Triage — alle anderen Module empfehlen wir dringend.',
      confirmCheckbox:
        'Ich habe meine Eingaben geprüft und bestätige, dass alle Angaben nach bestem Wissen vollständig und korrekt sind.',
      handoverButton: 'An Berater übergeben →',
      handoverDisabledHint: 'Bitte bestätigen Sie zuerst, dass Sie Ihre Eingaben geprüft haben.',
      handoverSuccessTitle: 'ÜBERGABE BESTÄTIGT.',
      handoverSuccessBody:
        'Ihr Berater wurde informiert, dass Ihre Eingaben aus Ihrer Sicht abgeschlossen sind. Sie können weiterhin Änderungen vornehmen — der Berater sieht jeweils den aktuellen Stand.',
      handoverAlreadyDone:
        'Bereits am {date} bestätigt — Sie können den Stand jederzeit aktualisieren.',
      handoverAgain: 'Erneut bestätigen',
      groupTriage: 'Triage',
      groupBusiness: 'Geschäftsmodell',
      groupResources: 'Ressourcen & Finanzen',
      groupContext: 'Privates Umfeld & Gesundheit',
      // Phase A: Stammdaten + Dokumente in der Übersicht
      stammdatenSectionTitle: 'Ihre Stammdaten',
      stammdatenChangeLink: 'Stammdaten ändern →',
      stammdatenEmpty: 'Sie haben noch keine Stammdaten erfasst.',
      stammdatenGoFill: 'Jetzt Stammdaten erfassen →',
      docsSectionTitle: 'Ihre hochgeladenen Dokumente',
      docsChangeLink: 'Andere Datei hochladen →',
      docsEmpty: 'Sie haben noch keine Dokumente hochgeladen.',
      docsGoUpload: 'Dokumente hochladen →',
    },
    onboarding: {
      skip: 'Tour überspringen',
      next: 'Weiter',
      back: 'Zurück',
      finish: 'Verstanden',
      progress: 'Schritt {current} von {total}',
      step1Eyebrow: 'Willkommen',
      step1Title: 'Ihr Self-Service-Portal.',
      step1Body:
        'Hier bereiten Sie alle Daten vor, die Ihre Beraterin oder Ihr Berater für die Tragfähigkeitsstellungnahme benötigt. Die Beratung kann nach AVGS § 45 SGB III durch das Jobcenter abgerechnet werden, wenn ein entsprechender Gutschein vorliegt.',
      step2Eyebrow: 'Ihre Module',
      step2Title: 'Reihenfolge frei wählbar.',
      step2Body:
        'Sie sehen unten Modul 00 (Stammdaten) und sieben weitere Module — die Triage als Pflicht-Erstkontakt sowie sechs inhaltliche Module. Sie können in beliebiger Reihenfolge arbeiten und jederzeit pausieren — alle Eingaben werden automatisch gesichert.',
      step3Eyebrow: 'Dokumente',
      step3Title: 'BWA und mehr.',
      step3Body:
        'Wenn Sie eine aktuelle Betriebswirtschaftliche Auswertung (BWA) haben, laden Sie diese unter „Anhang" hoch. Falls keine BWA vorliegt, können Sie die Werte auch direkt eintragen.',
      step4Eyebrow: 'Datenschutz',
      step4Title: 'Ihre Daten gehören Ihnen.',
      step4Body:
        'Wir verarbeiten Ihre Angaben ausschließlich auf Servern in der EU (Frankfurt am Main) und nur für die Tragfähigkeitsprüfung. Ausführliche Informationen finden Sie in unserer Datenschutzerklärung.',
      step4PrivacyLink: 'Datenschutzerklärung öffnen',
    },
    saveStatus: {
      saving: 'Eingaben werden gesichert',
      savedJustNow: 'Daten gesichert · gerade eben',
      savedSecondsAgo: 'Daten gesichert · vor {seconds} Sekunden',
      savedMinutesAgo: 'Daten gesichert · vor {minutes} Min.',
      savedHoursAgo: 'Daten gesichert · vor {hours} Std.',
      savedYesterday: 'Daten gesichert · gestern',
      idle: 'Auto-Save aktiv',
      error: 'Speichern fehlgeschlagen',
      retry: 'Erneut versuchen',
    },
    fieldHelp: {
      toggle: 'Erläuterung anzeigen',
      close: 'Schließen',
      whatLabel: 'Worum geht es?',
      whyLabel: 'Warum brauchen wir das?',
      exampleLabel: 'Beispiel',
      askAdvisorTitle: 'Ich weiß es nicht',
      askAdvisorBody:
        'Sie können diese Frage offen lassen. Ihre Beraterin oder Ihr Berater kann später nachfragen.',
      // Rückfragen-Channel — Mandanten-Sicht im FieldHelp
      askAdvisorAction: 'Frage an den Berater stellen →',
      askAdvisorFormTitle: 'Was ist Ihre Frage zu diesem Feld?',
      askAdvisorFormPlaceholder:
        'Beispiel: Welche Werte aus der BWA gehören in „Umsatz", wenn ich Privatentnahmen separat ausweise?',
      askAdvisorSubmit: 'Frage senden →',
      askAdvisorSubmitting: 'Wird gesendet …',
      askAdvisorSuccess:
        'Frage gesendet. Ihre Beraterin oder Ihr Berater bekommt eine Benachrichtigung.',
      askAdvisorError: 'Frage konnte nicht gesendet werden.',
      askAdvisorCancel: 'Abbrechen',
    },
    bwaUpload: {
      manualToggleOpen: 'Ich habe keine BWA — Daten manuell eintragen →',
      manualToggleClose: 'Manuelle Eingabe schließen',
      cameraButton: 'Foto vom BWA-Ausdruck aufnehmen',
      manualIntro:
        'Tragen Sie hier die durchschnittlichen monatlichen Werte ein. Eine grobe Schätzung genügt — Ihre Beraterin oder Ihr Berater wird die Werte mit Ihnen prüfen.',
    },
    stammdaten: {
      sectionLabel: 'Modul 00 — Stammdaten',
      moduleLabel: 'Ihre Stammdaten',
      moduleSubtitle: 'Persönliche Daten und Eckdaten Ihres Unternehmens.',
      title: 'IHRE STAMMDATEN.',
      firstTimeIntro:
        'Diese Daten brauchen wir für die formelle Stellungnahme. Sie können Sie später jederzeit korrigieren.',
      sectionPersonal: 'Zur Person',
      sectionBusiness: 'Zu Ihrem Unternehmen',
      fullNameLabel: 'Ihr Name',
      fullNamePlaceholder: 'Vor- und Nachname',
      birthdateLabel: 'Geburtsdatum',
      addressLabel: 'Anschrift',
      addressPlaceholder: 'Straße und Hausnummer\nPostleitzahl Stadt',
      branchLabel: 'Branche',
      branchPlaceholder: 'Bitte wählen …',
      branchOther: 'Andere — bitte Branche eingeben',
      branchOtherInputLabel: 'Branche (eigene Eingabe)',
      branchOtherPlaceholder: 'z.B. Imkerei, Klavierstimmen, ...',
      legalFormLabel: 'Rechtsform',
      legalFormPlaceholder: 'Bitte wählen …',
      legalFormOptions: {
        einzelunternehmen: 'Einzelunternehmen',
        gbr: 'GbR',
        ug: 'UG',
        gmbh: 'GmbH',
        partg: 'PartG',
        andere: 'Andere',
      },
      startDateLabel: 'Beginn der Selbständigkeit',
      taxIdLabel: 'Steuernummer (optional)',
      taxIdHint: 'Nur die Steuernummer Ihres Finanzamts — nicht die persönliche Steuer-ID.',
      taxIdPlaceholder: 'z.B. 21/123/45678',
      descriptionLabel: 'Kurzbeschreibung Ihrer Tätigkeit',
      descriptionHint: 'Was tun Sie konkret? Mindestens 10, maximal 500 Zeichen.',
      descriptionPlaceholder:
        'Beispiel: Buchhaltung und Lohnabrechnung für kleine Handwerksbetriebe in München.',
      descriptionCharCount: '{count} / 500 Zeichen',
      saveSuccess: 'Stammdaten gespeichert.',
      saveError: 'Stammdaten konnten nicht gespeichert werden.',
      saving: 'Wird gespeichert …',
      validationFutureDate: 'Datum darf nicht in der Zukunft liegen.',
      validationDescriptionMin: 'Bitte mindestens 10 Zeichen eingeben.',
      validationDescriptionMax: 'Maximal 500 Zeichen erlaubt.',
      validationRequired: 'Pflichtangabe.',
      mockHint: 'Demo-Modus — Stammdaten werden nicht in einer Datenbank gespeichert.',
    },
    documents: {
      sectionLabel: 'Anhang',
      sectionTitle: 'IHRE DOKUMENTE.',
      sectionIntro:
        'Laden Sie hier alle relevanten Unterlagen hoch. Ihre Beraterin oder Ihr Berater kann dann darauf zugreifen.',
      uploadButton: 'Datei auswählen',
      replaceButton: 'Andere Datei hochladen',
      addAnotherButton: 'Weitere Datei hinzufügen',
      removeButton: 'Entfernen',
      removeConfirm: 'Diese Datei wirklich entfernen?',
      statusEmpty: 'Noch nicht hochgeladen',
      statusUploaded: 'Hochgeladen am {date}',
      uploading: 'Lade hoch …',
      cameraButton: 'Foto aufnehmen',
      acceptedFormats: 'Akzeptiert: PDF, XLSX, JPG, PNG · max. 20 MB',
      errorTooLarge: 'Datei ist größer als 20 MB.',
      errorWrongType: 'Dieses Dateiformat wird nicht akzeptiert.',
      errorUploadFailed: 'Upload fehlgeschlagen.',
      errorEmpty: 'Datei ist leer.',
      errorRemoveFailed: 'Datei konnte nicht entfernt werden.',
      downloadButton: 'Öffnen',
      // Doc-Typ-Headlines (UI nutzt aber DOCUMENT_TYPES für die Labels)
    },
    clarifications: {
      sectionLabel: 'Nachrichten',
      sectionTitleInbox: 'NACHRICHTEN VOM BERATER.',
      sectionInboxIntro:
        'Ihre Beraterin oder Ihr Berater hat Rückfragen zu einzelnen Angaben. Bitte beantworten Sie diese, damit die Stellungnahme ohne Verzögerung erstellt werden kann.',
      sectionPendingTitle: 'Ihre offenen Fragen',
      sectionPendingIntro:
        'Sie warten noch auf eine Antwort zu folgenden Fragen. Wir geben Ihnen Bescheid, sobald Ihre Beraterin oder Ihr Berater geantwortet hat.',
      askedByMe: 'Von Ihnen',
      askedByBerater: 'Von Ihrer Beraterin / Ihrem Berater',
      onField: 'Zum Feld',
      askedAt: 'Gefragt am {date}',
      answeredAt: 'Beantwortet am {date}',
      answerLabel: 'Ihre Antwort',
      answerPlaceholder: 'Bitte beantworten Sie die Frage so konkret wie möglich.',
      sendAnswer: 'Antwort senden →',
      sending: 'Wird gesendet …',
      sendError: 'Antwort konnte nicht gesendet werden.',
      emptyState: 'Aktuell keine offenen Rückfragen.',
    },
  },

  upload: {
    bwa: {
      dropTitle: 'BWA-Datei hierhin ziehen',
      dropOr: 'oder',
      browseButton: 'Datei auswählen',
      acceptedFormats: 'Akzeptiert: XLSX, PDF · max. 20 MB',
      uploading: 'Lade hoch …',
      progress: '{percent} %',
      successFile: '{name} erfolgreich hochgeladen am {date}',
      replaceButton: 'Andere Datei hochladen',
      errorTooLarge: 'Datei ist größer als 20 MB.',
      errorWrongType: 'Nur XLSX und PDF sind erlaubt.',
      errorUploadFailed: 'Upload fehlgeschlagen.',
      errorPersistFailed:
        'Datei wurde hochgeladen, der Verweis konnte aber nicht gespeichert werden.',
    },
  },

  privacy: {
    title: 'DATENSCHUTZ.',
    subtitle: 'Wie wir Ihre Daten verarbeiten – nüchtern und vollständig.',
    body: 'Diese Seite ist ein Skelett für die finale Datenschutzerklärung. Sie wird vor dem produktiven Start vollständig ausformuliert.',
  },

  footer: {
    privacyLink: 'Datenschutz',
    imprintLink: 'Impressum',
    operator: 'Petschwork Consulting · Tragfähigkeitsprüfung nach SGB II',
    dataLocation: 'Datenverarbeitung in der EU (Frankfurt am Main)',
  },

  errors: {
    notFound: 'Diese Seite existiert nicht.',
    notFoundCta: 'Zur Startseite',
    requireLogin: 'Bitte melden Sie sich an, um diese Seite zu öffnen.',
  },

  // ===========================================================================
  // DSGVO-Self-Service (Mandant + Berater + gemeinsame Labels)
  // Backend: supabaseDsgvo.ts + Migrationen 0019/0020/0021.
  // ===========================================================================
  dsgvo: {
    requestTypes: {
      data_export: 'Datenexport',
      deletion: 'Datenlöschung',
      consent_withdrawal: 'Einwilligungs-Widerruf',
      rectification: 'Berichtigung / Sonstiges',
    },
    requestTypesLong: {
      data_export: 'Datenexport (Art. 15 DSGVO)',
      deletion: 'Datenlöschung (Art. 17 DSGVO)',
      consent_withdrawal: 'Widerruf einer Einwilligung (Art. 7 Abs. 3 DSGVO)',
      rectification: 'Berichtigung / sonstige Anfrage (Art. 16/18/21 DSGVO)',
    },
    requestStatuses: {
      open: 'Offen',
      in_progress: 'In Bearbeitung',
      fulfilled: 'Erfüllt',
      rejected: 'Abgelehnt',
      expired: 'Abgelaufen',
    },
    consentPurposes: {
      tragfaehigkeitspruefung: 'Tragfähigkeitsprüfung',
      jobcenter_uebermittlung: 'Übermittlung an das Jobcenter',
      mandanten_coaching: 'Begleitende Beratung / Coaching',
      speicherung_abrechnungsdaten: 'Speicherung der Abrechnungsdaten',
    },
    selfService: {
      // Header
      sectionLabel: 'Ihre Datenschutz-Rechte',
      title: 'MEINE DATEN.',
      subtitle: 'Sie haben jederzeit Kontrolle über Ihre Daten.',
      backLink: 'Zur Mandanten-Übersicht',

      // Demo-Banner
      demoBanner:
        'Demo-Modus — Ihre DSGVO-Anfragen in dieser Vorschau werden NICHT gespeichert. In der echten Anwendung werden alle Anfragen DSGVO-konform protokolliert und Ihrem Berater übermittelt.',

      // Sektion: Datenexport (Art. 15)
      exportLabel: 'Art. 15 DSGVO',
      exportTitle: 'MEINE DATEN HERUNTERLADEN.',
      exportIntro:
        'Sie haben das Recht, eine vollständige Kopie aller über Sie gespeicherten Daten zu erhalten. Der Export wird als JSON-Datei (maschinenlesbar) erzeugt.',
      exportContains: 'Enthalten ist:',
      exportListItem1: 'Ihre Stammdaten (Name, Geburtsdatum, Anschrift, Geschäftsdaten)',
      exportListItem2: 'Alle Eingaben aus Ihren Wizard-Modulen',
      exportListItem3: 'Hochgeladene Dokumente (Metadaten, ohne Datei-Inhalt)',
      exportListItem4: 'Rückfragen-Verlauf zwischen Ihnen und Ihrem Berater',
      exportListItem5: 'Erteilte und widerrufene Einwilligungen',
      exportListItem6: 'Aggregierte Audit-Zusammenfassung (Anzahl Zugriffe, Zeitspanne)',
      exportButton: 'Datenexport als JSON-Datei herunterladen →',
      exportButtonRunning: 'Export wird vorbereitet …',
      exportPrintAlternative:
        'Alternativ: Druckansicht „Meine Eingaben" — als PDF speichern oder ausdrucken',
      exportPrintButton: 'Druckansicht öffnen →',
      exportSuccess:
        'Datei wurde heruntergeladen. Falls Sie weitere Auskunft brauchen, können Sie unten eine Berichtigungs- oder sonstige Anfrage stellen.',
      exportError: 'Datenexport ist fehlgeschlagen.',
      exportNoteAuditLog:
        'Der vollständige Audit-Log enthält Angaben anderer Berater (IP-Adressen, User-Agents) und wird aus Datenschutzgründen für andere Personen NICHT mitexportiert. Sie erhalten eine aggregierte Zusammenfassung — auf Wunsch der Aufsichtsbehörde liegt das Volldetail nur für deren Einsicht vor.',

      // Sektion: Löschung (Art. 17)
      deletionLabel: 'Art. 17 DSGVO',
      deletionTitle: 'LÖSCHUNG MEINER DATEN BEANTRAGEN.',
      deletionIntro:
        'Sie können die Löschung Ihrer Daten verlangen. Wichtig: Bei B2G-Mandaten (Jobcenter-Beauftragung) müssen wir laut SGB-X-Aufbewahrungspflichten 10 Jahre archivieren — die Anfrage wird in diesem Fall mit Hinweis auf die Rechtsgrundlage abgelehnt.',
      deletionReasonLabel: 'Begründung Ihrer Löschanfrage (optional, max. 2000 Zeichen)',
      deletionReasonPlaceholder:
        'Beispiel: Ich wünsche keine weitere Beratung und bitte um Löschung meines Mandats.',
      deletionReasonCharCount: '{count} / 2000 Zeichen',
      deletionButton: 'Löschanfrage stellen →',
      deletionButtonRunning: 'Anfrage wird gesendet …',
      deletionConfirmTitle: 'Löschung wirklich beantragen?',
      deletionConfirmBody:
        'Mit dem Klick auf „Anfrage absenden" wird Ihre Löschanfrage Ihrem Berater zugestellt. Er hat 30 Tage Zeit zur Bearbeitung. Solange die Anfrage offen ist, können Sie keine zweite Löschanfrage stellen.',
      deletionConfirmCancel: 'Abbrechen',
      deletionConfirmSubmit: 'Anfrage absenden',
      deletionExistingTitle: 'Löschanfrage bereits gestellt',
      deletionExistingHint:
        'Eine Löschanfrage vom {date} wartet noch auf Bearbeitung durch Ihren Berater (Frist: {deadline}).',
      deletionSuccess:
        'Anfrage eingegangen am {date}. Ihr Berater wurde benachrichtigt. Frist zur Bearbeitung: {deadline}.',
      deletionError: 'Löschanfrage konnte nicht gesendet werden.',

      // Sektion: Einwilligungen widerrufen (Art. 7 Abs. 3)
      consentLabel: 'Art. 7 Abs. 3 DSGVO',
      consentTitle: 'EINWILLIGUNG WIDERRUFEN.',
      consentIntro:
        'Sie können erteilte Einwilligungen jederzeit widerrufen. Der Widerruf wirkt für die Zukunft — bereits erfolgte Verarbeitungen bleiben rechtmäßig.',
      consentEmpty:
        'Es liegen noch keine erteilten Einwilligungen vor, oder Ihre Einwilligungen wurden bereits widerrufen.',
      consentStatusActive: 'Aktiv',
      consentStatusWithdrawn: 'Widerrufen',
      consentGrantedAt: 'Erteilt am {date}',
      consentWithdrawnAt: 'Widerrufen am {date}',
      consentWithdrawButton: 'Widerrufen →',
      consentWithdrawing: 'Wird widerrufen …',
      consentWithdrawSuccess: 'Einwilligung wurde widerrufen.',
      consentWithdrawError: 'Widerruf konnte nicht gespeichert werden.',

      // Sektion: Anfragen-Status
      requestsLabel: 'Verlauf',
      requestsTitle: 'MEINE DSGVO-ANFRAGEN — STATUS.',
      requestsIntro:
        'Übersicht aller von Ihnen gestellten DSGVO-Anfragen und deren Bearbeitungsstand.',
      requestsEmpty: 'Sie haben noch keine DSGVO-Anfrage gestellt.',
      requestRequestedAt: 'Gestellt am {date}',
      requestDeadline: 'Frist bis {date}',
      requestDeadlineDays: 'Noch {days} Tage Frist',
      requestDeadlineExpired: 'Frist abgelaufen',
      requestHandledAt: 'Bearbeitet am {date}',
      requestResponse: 'Antwort des Beraters',
      requestReason: 'Ihre Begründung',

      // Sektion: Weitere Rechte
      otherRightsLabel: 'Weitere Rechte',
      otherRightsTitle: 'WEITERE RECHTE.',
      rectificationTitle: 'Berichtigung (Art. 16 DSGVO)',
      rectificationBody:
        'Bitte korrigieren Sie Stammdaten direkt im Wizard (Modul 00). Wenn das nicht möglich ist, beschreiben Sie unten kurz, welche Berichtigung Sie wünschen.',
      restrictionTitle: 'Einschränkung der Verarbeitung (Art. 18 DSGVO)',
      restrictionBody:
        'Sie können verlangen, dass Ihre Daten nur noch eingeschränkt verarbeitet werden — z. B. während eine Berichtigung geprüft wird.',
      portabilityTitle: 'Datenübertragbarkeit (Art. 20 DSGVO)',
      portabilityBody:
        'Die Datenexport-Funktion oben deckt das Recht auf Datenübertragbarkeit ab — JSON ist ein gängiges, maschinenlesbares Format.',
      objectionTitle: 'Widerspruch (Art. 21 DSGVO)',
      objectionBody:
        'Bei Widerspruch gegen die Verarbeitung wenden Sie sich an Ihren Berater oder die zuständige Aufsichtsbehörde.',
      complaintTitle: 'Beschwerderecht (Art. 77 DSGVO)',
      complaintBody:
        'Sie können sich jederzeit bei einer Datenschutz-Aufsichtsbehörde beschweren. Bundesweite Anlaufstelle ist der/die Bundesbeauftragte für den Datenschutz und die Informationsfreiheit (BfDI):',
      complaintLink: 'bfdi.bund.de — Beschwerde einreichen',
      otherRequestModalTitle: 'Sonstige DSGVO-Anfrage stellen',
      otherRequestPlaceholder:
        'Bitte beschreiben Sie kurz, was Sie wünschen (z. B. Einschränkung der Verarbeitung, Berichtigung eines Felds, Widerspruch).',
      otherRequestSubmit: 'Anfrage absenden →',
      otherRequestCancel: 'Abbrechen',
      otherRequestSuccess: 'Anfrage wurde Ihrem Berater zugestellt.',
      otherRequestError: 'Anfrage konnte nicht gesendet werden.',
      otherRequestOpenButton: 'Sonstige Anfrage stellen →',

      // Footer
      footerControllerLabel: 'Verantwortlicher',
      footerControllerName: 'Petschwork Consulting GmbH',
      footerControllerAddress:
        'Petschwork Consulting GmbH · Pettenkoferstr. 4 · 80336 München · datenschutz@petschwork.de',
      footerDpoLabel: 'Datenschutzbeauftragte:r',
      footerDpoBody:
        'Aktuell ist keine externe DSB-Stelle bestellt (§ 38 BDSG-Schwellenwerte nicht überschritten). Datenschutzanfragen erreichen uns über die oben angegebene E-Mail-Adresse.',
      footerSupervisoryLabel: 'Aufsichtsbehörde',
      footerSupervisoryBody:
        'Bundesbeauftragte:r für den Datenschutz und die Informationsfreiheit (BfDI), Graurheindorfer Straße 153, 53117 Bonn — bfdi.bund.de',
    },

    // Berater-Cockpit-Sektion
    cockpit: {
      sectionLabel: 'DSGVO',
      sectionTitle: 'DSGVO-ANFRAGEN.',
      sectionIntro:
        '30-Tage-Bearbeitungsfrist nach Art. 12 Abs. 3 DSGVO. Frist-abgelaufene Anfragen müssen an den/die DSB eskaliert werden.',
      tabOpen: 'Offen',
      tabInProgress: 'In Bearbeitung',
      tabHistory: 'Verlauf',
      empty: 'Keine offenen DSGVO-Anfragen.',
      emptyHistory: 'Bisher noch keine erledigten DSGVO-Anfragen.',
      requestedBy: 'Mandant',
      requestedAt: 'gestellt am {date}',
      deadlineRemaining: 'noch {days} Tage Frist',
      deadlineExpired: 'FRIST ABGELAUFEN',
      deadlineEscalation: 'Eskalation an DSB nötig',
      reasonLabel: 'Begründung des Mandanten',
      reasonEmpty: '— keine Begründung angegeben —',
      handledAt: 'bearbeitet am {date}',
      handlerLabel: 'Bearbeitet von',
      responseLabel: 'Antwort an Mandant',

      // Action buttons (per type)
      actionExportDownload: 'Datenexport als JSON erzeugen + herunterladen',
      actionExportDownloading: 'Export wird erzeugt …',
      actionExportFulfill: 'Datenexport als erfüllt markieren',
      actionExportHint:
        'Laden Sie den Export herunter und versenden Sie ihn dem Mandanten per gesondertem Kanal (verschlüsselte E-Mail oder Übergabe). Nach Versand bitte unten als erfüllt markieren.',
      actionDeleteConfirm: 'Löschung bestätigen + ausführen',
      actionDeleteRunning: 'Wird ausgeführt …',
      actionDeleteConfirmTitle: 'Löschung wirklich ausführen?',
      actionDeleteConfirmBody:
        'Der Mandant wird soft-deleted (deleted_at gesetzt). Hard-Delete erfolgt nach Aufbewahrungsfrist über das Retention-Job. Diese Aktion ist nicht umkehrbar.',
      actionDeleteConfirmYes: 'Ja, Löschung ausführen',
      actionDeleteConfirmNo: 'Abbrechen',
      actionWithdrawalAck: 'Widerruf bestätigen',
      actionGenericFulfill: 'Anfrage als erfüllt markieren',

      actionRejectOpen: 'Mit Begründung ablehnen',
      actionRejectModalTitle: 'DSGVO-Anfrage ablehnen',
      actionRejectModalIntro:
        'Bitte begründen Sie die Ablehnung sachlich und mit Verweis auf die Rechtsgrundlage. Der Mandant erhält Ihre Begründung als Antwort.',
      actionRejectExampleDeletionB2G:
        'Beispiel B2G: „Wir sind nach § 84 SGB X verpflichtet, Akten zur Tragfähigkeitsprüfung 10 Jahre aufzubewahren. Eine Löschung ist erst zum {{Datum}} möglich. Bis dahin werden Ihre Daten ausschließlich für Aufsichts- und Beweissicherungszwecke vorgehalten."',
      actionRejectReasonLabel: 'Begründung der Ablehnung (Pflicht, max. 4000 Zeichen)',
      actionRejectReasonPlaceholder: 'Sachlich, mit Rechtsgrundlage, ohne Werturteile.',
      actionRejectReasonCharCount: '{count} / 4000 Zeichen',
      actionRejectSubmit: 'Ablehnung absenden',
      actionRejectCancel: 'Abbrechen',
      actionRejectError: 'Ablehnung konnte nicht gespeichert werden.',
      actionFulfillError: 'Aktion ist fehlgeschlagen.',

      // Cases-Liste-Badge
      casesListBadge: 'DSGVO-Anfrage offen · {days} Tage',
      casesListBadgeExpired: 'DSGVO-FRIST abgelaufen',
    },
  },

  // Print-Export Mandanten-Eingaben
  print: {
    headerLabel: 'Druckansicht',
    title: 'MEINE EINGABEN.',
    subtitle: 'Stand der erfassten Daten — geeignet zum Ausdruck oder Speichern als PDF.',
    printButton: 'Drucken / als PDF speichern →',
    backLink: 'Zur Mandanten-Übersicht',
    exportedAt: 'Stand: {date}',
    caseLabelMandant: 'Mandant:in',
    caseLabelReference: 'Aktenzeichen',
    caseLabelCaseId: 'Mandats-ID',
    sectionStammdaten: 'Stammdaten',
    sectionDocuments: 'Hochgeladene Dokumente',
    sectionModules: 'Wizard-Module',
    sectionFooter: 'Verantwortlich für die Datenverarbeitung',
    fieldEmpty: '— noch nicht ausgefüllt —',
    docLine: '{filename} ({type}) · hochgeladen {date}',
    moduleStatus: 'Status',
    moduleEntries: 'Eingaben',
    footerNote:
      'Diese Druckansicht enthält ausschließlich Daten, die Sie selbst in der Tragfähigkeits-App eingegeben haben. Bewertungs- oder Diagnosetexte des Beraters sind NICHT enthalten.',
  },
} as const;

export type De = typeof de;
